import { fetchCached, invalidateCachedRequests } from './request-cache';

const DASHBOARD_CACHE_TTL_MS = 60_000;
const FEED_CACHE_TTL_MS = 20_000;

export interface TeacherSelectionApi {
  _id?: string | { $oid?: string };
  id?: string;
  studentUserId?: string | { $oid?: string };
  teacherId: string;
  teacherName: string;
  studentName?: string;
  subject: string;
  subjectId?: string;
  classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
  preferredTimeSlot?: string;
  notes?: string;
  studentRating?: number;
  studentReview?: string;
  sessionStatus?: 'SCHEDULED' | 'COMPLETED';
  completedAt?: string;
  sessionDailyStatus?: Record<string, 'SCHEDULED' | 'COMPLETED'>;
  meetingLink?: string;
  meetingCode?: string;
  selectedAt: string;
  startDate?: string;
  endDate?: string;
  rescheduledDateTimes?: Record<string, string>;
}

export interface SubjectHierarchyNode {
  _id?: string;
  id?: string;
  displayName?: string;
  name?: string;
  class?: string;
  stream?: string;
  exam?: string;
  activityType?: string;
}

export interface ReferralSummary {
  referralCode?: string;
  referralLink?: string;
}

export interface TeacherSubscriptionStatus {
  hasActiveSubscription: boolean;
  planName?: string;
  expiresAt?: string;
}

export interface StudentProfile {
  dateOfBirth?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER' | 'PREFER_NOT_TO_SAY';
  currentLocation?: string;
  address?: string;
  classSubjectCombinations?: Array<{
    type: 'ACADEMIC' | 'COMPETITIVE' | 'ACTIVITY';
    className: string;
    subjectIds: string[];
    curriculum?: string;
  }>;
  interests?: string[];
  hobbies?: string[];
  grade?: string;
  plannedGrade?: string;
  school?: string;
}

export const normalizeEntityId = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && '$oid' in (value as Record<string, unknown>)) {
    return String((value as { $oid?: string }).$oid || '');
  }
  return String(value);
};

export const resolveSelectionId = (selection: Pick<TeacherSelectionApi, 'id' | '_id'>): string => {
  if (selection.id) return selection.id;
  if (typeof selection._id === 'string') return selection._id;
  if (selection._id && typeof selection._id === 'object' && selection._id.$oid) {
    return selection._id.$oid;
  }
  return '';
};

export const slotToDate = (baseIso: string, preferredTimeSlot?: string) => {
  const base = new Date(baseIso);
  if (!preferredTimeSlot) return base.toISOString();
  const timePart = preferredTimeSlot.includes('|')
    ? preferredTimeSlot.split('|')[1]?.trim()
    : preferredTimeSlot.trim();
  if (!timePart) return base.toISOString();
  const startPart = timePart.split('-')[0]?.trim();
  if (!startPart) return base.toISOString();
  const match = startPart.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return base.toISOString();
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'PM' && hour < 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  base.setHours(hour, minute, 0, 0);
  return base.toISOString();
};

export const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

export const buildSubjectLabelMap = (hierarchy: Record<string, unknown>) => {
  const nextMap: Record<string, string> = {};

  Object.values(hierarchy || {}).forEach((categories) => {
    Object.values((categories as Record<string, unknown>) || {}).forEach((group) => {
      Object.values((group as Record<string, unknown>) || {}).forEach((items) => {
        (Array.isArray(items) ? items : []).forEach((subject) => {
          const subjectNode = subject as SubjectHierarchyNode;
          const subjectId = String(subjectNode?._id || subjectNode?.id || '').trim();
          if (!subjectId) return;
          const primary = String(subjectNode?.displayName || subjectNode?.name || '').trim();
          const details = [
            subjectNode?.class,
            subjectNode?.stream,
            subjectNode?.exam,
            subjectNode?.activityType,
          ]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(' | ');
          const label = details ? `${primary} (${details})` : primary;
          if (label) nextMap[subjectId] = label;
        });
      });
    });
  });

  return nextMap;
};

export const dashboardDataService = {
  getTeacherSelections: (force = false) =>
    fetchCached<TeacherSelectionApi[]>('/teachers/selections/teacher', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS,
      force,
    }),
  getStudentSelections: (force = false) =>
    fetchCached<TeacherSelectionApi[]>('/teachers/selections/student', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS,
      force,
    }),
  getSubjectLabelMap: async (force = false) => {
    const hierarchy = await fetchCached<Record<string, unknown>>('/subjects/hierarchy', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS * 5,
      force,
    });
    return buildSubjectLabelMap(hierarchy || {});
  },
  getReferralSummary: (force = false) =>
    fetchCached<ReferralSummary>('/users/me/referral', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS,
      force,
    }),
  getTeacherSubscriptionStatus: (force = false) =>
    fetchCached<TeacherSubscriptionStatus>('/teachers/subscription/status', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS,
      force,
    }),
  getStudentProfile: (force = false) =>
    fetchCached<StudentProfile>('/students/profile', undefined, {
      ttlMs: DASHBOARD_CACHE_TTL_MS,
      force,
    }),
  invalidateTeacherSelections: () => invalidateCachedRequests('/teachers/selections/teacher'),
  invalidateStudentSelections: () => invalidateCachedRequests('/teachers/selections/student'),
  invalidateReferralSummary: () => invalidateCachedRequests('/users/me/referral'),
  getFeedCacheTtl: () => FEED_CACHE_TTL_MS,
};
